import React, { memo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { themeGet } from '@styled-system/theme-get'
import { Flex } from 'pcln-design-system'
import Account from '../Account'
import NavLink from '../NavLink'
import alignmentToJustify from '../../utils/alignmentToJustify'
import { headerNavigationShape, alignmentTypes } from '../../utils/shapes'
import Dropdown from '../Dropdown'

const Nav = styled.nav`
  &&& {
    display: flex;
    height: 100%;
    margin: 0 ${themeGet('space.4')};
    max-width: 1440px;
    width: 100%;
  }
`

const Content = styled(Flex)`
  position: relative;
  width: 100%;
`

const LinksContainer = styled(Flex)`
  display: flex;
  justify-content: ${(props) => props.justifyContent};
  position: relative;
  white-space: nowrap;
  width: 100%;

  &&& a,
  button {
    font-size: ${themeGet('fontSizes.1')}px;
  }

  &&& a:not(:last-child),
  div:not(:last-child) {
    margin-right: ${themeGet('space.4')};
  }
`

function Navigation({
  align,
  appName,
  color,
  dropdownColor,
  dropdownWidth,
  iconColor,
  includeAccount,
  navigation,
  target,
  ...props
}) {
  const justifyContent = alignmentToJustify(align)

  return (
    <Nav>
      {navigation.length > 0 && (
        <Content id='primary-nav' alignItems='center'>
          <LinksContainer
            id='global-header-nav-section'
            data-testid='nav-links-container'
            justifyContent={justifyContent}
            center={align === 'center'}
          >
            {navigation.map((link) => {
              const {
                acceptedApps,
                bold,
                fillColor,
                href,
                iconName,
                isMobileOnly,
                label,
                links,
                ...linkProps
              } = link

              if ((!href && !links) || isMobileOnly) {
                return null
              }

              const isActiveNavLink = acceptedApps?.includes(appName)
              const fontWeight = bold ? '700' : '500'
              return links && links.length > 0 ? (
                <Dropdown
                  key={`lg-dropdown-${label}`}
                  color={color}
                  dropdownColor={dropdownColor}
                  dropdownWidth={dropdownWidth}
                  fontWeight={fontWeight}
                  link={link}
                  target={target}
                />
              ) : (
                <NavLink
                  {...props}
                  {...linkProps}
                  key={`lg-navlink-${label}`}
                  color={fillColor ? fillColor : isActiveNavLink ? 'primary' : color}
                  fontWeight={isActiveNavLink ? '700' : fontWeight}
                  href={href}
                  iconColor={iconColor}
                  iconName={iconName}
                  label={label}
                  links={links}
                  target={target}
                />
              )
            })}
            {includeAccount && <Account />}
          </LinksContainer>
        </Content>
      )}
    </Nav>
  )
}

Navigation.displayName = 'Navigation'

Navigation.propTypes = {
  appName: PropTypes.string,
  align: alignmentTypes,
  color: PropTypes.string,
  dropdownColor: PropTypes.string,
  dropdownWidth: PropTypes.string,
  includeAccount: PropTypes.bool,
  iconColor: PropTypes.string,
  navigation: PropTypes.arrayOf(headerNavigationShape).isRequired,
  target: PropTypes.string,
}

Navigation.defaultProps = {
  align: 'center',
}

export default memo(Navigation)
